/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */
import React, { useEffect, useState, useRef } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { MapboxGLMap } from 'components/mapboxglmap'
import { MetaInfos } from 'components/metaInfos'
import { MapActivationButton } from 'components/mapActivationButton'
import useSize from 'hooks/useSize'
import { config } from './config'
import { HazardLevels } from 'components/hazardLevels'
import { PerimeterLevels } from 'components/perimeterLevels'
import { text as text_de, locales as locales_de } from 'components/locales_de'
import { text as text_fr, locales as locales_fr } from 'components/locales_fr'
import { LevelMeaning } from './levelMeaning'
import { SourceTable } from 'components/sourceTable'
import { NominatimSearch } from 'components/nominatimSearch'
import { Hint } from 'components/Hint'
import mapboxgl from 'mapbox-gl'
import { SectionHead } from 'components/SectionHead'
import centroid from 'turf-centroid'
import { levelNames, hazardNames, perimeterNames, languageType } from './types'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'

const sourceData_de = require('../tamedia_hazard_sources_de.json')
const sourceData_fr = require('../tamedia_hazard_sources_fr.json')
const article_id_de = '928275021358'
const article_id_fr = '916068879717'
const teaser_DE: string[] = ['169965351181']
const teaser_FR: string[] = []
//
//
let language: languageType = 'fr' as languageType
//
//
const SOURCEDATA = language === 'de' ? sourceData_de : sourceData_fr
const TEXT = language === 'de' ? text_de : text_fr
const LOCALES = language === 'de' ? locales_de : locales_fr
const ARTICLE_ID = language === 'de' ? article_id_de : article_id_fr
const ARTICLETEASER = language === 'de' ? teaser_DE : teaser_FR

export function AppComponent() {
  //
  /* State */
  //
  const [container, size] = useSize()
  const [map, registerMap] = useState<InterfaceMapBox | undefined>(undefined)
  const mapAnchor = useRef<HTMLDivElement | null>(null)
  const [mapOptions, setMapOptions] = useState<{
    isFullscreen: boolean
    isActive: boolean
    hiddenHazards: string[]
  }>({ isFullscreen: false, isActive: false, hiddenHazards: [] })
  const [activationused, setactivationused] = useState<boolean>(false)
  const [hazardused, sethazardused] = useState<boolean>(false)
  //
  // Activate Map
  const setMapIsActive = (current: boolean) => {
    setactivationused(true)
    let newOptions = Object.assign({}, mapOptions)
    newOptions.isActive = current
    if (size.isMobile && current === true) newOptions.isFullscreen = current
    setMapOptions(newOptions)
  }
  // Toggle selected hazards in state
  const toggleHazards = (e: React.MouseEvent) => {
    if (!e.nativeEvent.srcElement) return
    sethazardused(true)
    let hazard = e.nativeEvent.srcElement.id
    let newOptions = Object.assign({}, mapOptions)

    if (newOptions.hiddenHazards.length === 0) {
      newOptions.hiddenHazards = config.allHazards.filter(
        item => item !== hazard
      )
    } else if (newOptions.hiddenHazards.length === 3) {
      if (newOptions.hiddenHazards.indexOf(hazard) === -1) {
        newOptions.hiddenHazards = []
      } else {
        newOptions.hiddenHazards = config.allHazards.filter(
          item => item !== hazard
        )
      }
    }

    setMapOptions(newOptions)
  }
  // Show and Hide Hazard-Layers
  const showAndHideHazards = () => {
    if (!map) return
    let layers = map.getStyle().layers
    config.allHazards.forEach((hazard: string) => {
      let hazardIsHidden = mapOptions.hiddenHazards.includes(hazard)

      layers?.map(l => {
        if (l.id.includes('_' + hazard + '_')) {
          if (!hazardIsHidden) {
            if (
              !l.id.includes('peri_' + hazard) ||
              mapOptions.hiddenHazards.length === 3
            ) {
              map.setLayoutProperty(l.id, 'visibility', 'visible')
            } else {
              map.setLayoutProperty(l.id, 'visibility', 'none')
            }
          } else {
            map.setLayoutProperty(l.id, 'visibility', 'none')
          }
        }
      })
    })
  }
  // Show and Hide Hazard-Layers
  useEffect(() => {
    if (!map) return
    showAndHideHazards()
  }, [mapOptions.hiddenHazards])

  return (
    <div className='index' ref={container}>
      <ThemeSwitch />
      <Masthead articleId={ARTICLE_ID} />
      <header className='ui vertical segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>{TEXT.head.title}</h1>
            <h2>{TEXT.head.lead}</h2>
          </div>
        </div>
      </header>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <MetaInfos
            authors={[
              'Patrick Meier',
              'Duc-Quang Nguyen',
              'Dominik Balmer',
              'Dominique Botti',
              'Sylvain Besson',
              'Oliver Zihlmann',
              'Sebastian Broschinski'
            ]}
            updated={'26. Juli 2020'}
            language={language}
          />
          <div className='ta TEXT'>{TEXT.main}</div>{' '}
          <Hint big>{TEXT.disclaimer}</Hint>
          <SectionHead>{LOCALES.subsections.key}</SectionHead>
          <HazardLevels locales={LOCALES} />
          <PerimeterLevels locales={LOCALES} />
        </div>
      </div>
      <div className='ui text container nominatim'>
        <SectionHead>{LOCALES.subsections.search}</SectionHead>

        {map && (
          <NominatimSearch
            map={map}
            locales={LOCALES.nominatim}
            language={language}
          />
        )}
      </div>
      <MapboxGLMap
        isactive={mapOptions.isActive}
        toggleActivation={setMapIsActive}
        language={language}
        locales={LOCALES}
        getMap={map => {
          var layers = map.getStyle().layers

          // Create marker for user position
          var el = document.createElement('div')
          el.className = 'marker'
          el.style.backgroundImage = 'url(' + require('images/marker.svg') + ')'
          map.marker = new mapboxgl.Marker(el)
            .setLngLat([0, 0])
            .addTo((map as unknown) as mapboxgl.Map)

          // Pattern für Perimeter
          map.addImageToMap(require('images/icons_pattern.png'), 'stroked')
          map.addImageToMap(
            require('images/icons_pattern_big.png'),
            'stroked_big'
          )
          map.addImageToMap(require('images/icons_pattern_2.png'), 'stroked_2')
          map.addImageToMap(
            require('images/icons_pattern_2_big.png'),
            'stroked_2_big'
          )
          // create popup
          let popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
          })
          // layer-ids for not/not fully checked areas
          let periLayers = [
            'peri_rockfall_partial',
            'peri_rockfall_not',
            'peri_landslide_partial',
            'peri_landslide_not',
            'peri_water_partial',
            'peri_water_not',
            'peri_avalanche_partial',
            'peri_avalanche_not'
          ]
          // add popup/tooltip for not/not fully checked areas
          periLayers.forEach(peri => {
            map.on('mousemove', peri, e => {
              if (!e.features || e.features.length === 0) return
              var coordinates = centroid(e.features[0]).geometry.coordinates
              map.getCanvas().style.cursor = 'pointer'

              if (e.features[0].properties) {
                popup
                  .setLngLat(coordinates)
                  .setHTML(
                    '<div class="entry"><b>' +
                      e.features[0].properties.na +
                      '</b><br/>' +
                      LOCALES.perimeter.steps[
                        peri.split('_')[2] as perimeterNames
                      ] +
                      '</div>'
                  )
                  .addTo(map)
              }
            })
            map.on('mouseleave', peri, e => {
              map.getCanvas().style.cursor = ''

              popup.remove()
            })
          })

          // Get Hazard-Layer Ids
          let hazardLayers = layers
            ?.filter(
              f =>
                f.id.includes('_slight') ||
                f.id.includes('_mean') ||
                f.id.includes('_substantial')
            )
            ?.map(m => m.id)
          // Add Tooltip for Hazard Layers
          hazardLayers?.forEach(peri => {
            map.on('mousemove', peri, e => {
              let coordinates = e.lngLat
              var features = map.queryRenderedFeatures(e.point)

              let list: string[] = []
              features.forEach(feat => {
                if (feat.properties) {
                  let h: hazardNames = feat.properties.h
                  let l: levelNames = feat.properties.l
                  if (h && l) {
                    let el =
                      "<div class='entry " +
                      l +
                      "' style='background-color:" +
                      config.colors.hazards[h][l] +
                      "'><img  src='" +
                      require('images/icons_' + h + '.svg') +
                      "'/>" +
                      LOCALES.hazards[h] +
                      ' - ' +
                      LOCALES.levels[l] +
                      '</div>'
                    if (list.indexOf(el) === -1) list.push(el)
                  }
                }
              })
              ///
              map.getCanvas().style.cursor = 'pointer'
              popup
                .setLngLat(coordinates)
                .setHTML(list.join(''))                  .setMaxWidth('400px')

                .addTo(map)
            })
            map.on('mouseleave', peri, e => {
              map.getCanvas().style.cursor = ''

              popup.remove()
            })
          })
          // Map zum State
          registerMap(map)
        }}
      >
        <>
          <MapActivationButton
            current={mapOptions.isActive}
            toggle={setMapIsActive}
            locales={size.isMobile ? LOCALES.map.mobile : LOCALES.map.desktop}
          />
          {!hazardused && (
            <span className='tooltip bottom hazards'>
              {LOCALES.hints.hazards}
            </span>
          )}
          {!activationused && !size.isMobile && (
            <span className='tooltip top activation'>
              {LOCALES.hints.interaction}
            </span>
          )}

          <div className='hazardswitchcontainer'>
            {config.allHazards.map((h, i) => (
              <button
                key={i}
                id={h}
                onClick={toggleHazards}
                style={{
                  backgroundColor: config.colors.hazards[h].mean
                }}
                className={
                  'hazardswitch ' +
                  h +
                  ' ' +
                  (mapOptions.hiddenHazards.indexOf(h) !== -1 ? 'off' : '')
                }
              >
                <img src={require('images/icons_' + h + '.svg')} />
                {(!size.isMobile ||
                  (mapOptions.hiddenHazards.indexOf(h) === -1 &&
                    mapOptions.hiddenHazards.length === 3)) && (
                  <div>{LOCALES.hazards[h as hazardNames]}</div>
                )}
              </button>
            ))}
          </div>
        </>
      </MapboxGLMap>{' '}
      <>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta TEXT'>
              <h4 className='title'>{TEXT.levelmeaning.title}</h4>
              <div className='sub'>{TEXT.levelmeaning.sub}</div>
              {Object.keys(TEXT.levelmeaning.levels).map((l, i) => {
                return (
                  <React.Fragment key={i}>
                    <LevelMeaning level={l as levelNames} text={TEXT} />
                  </React.Fragment>
                )
              })}
              <div className='zonedefinitionwrapper'>
                <h4 className='title'>{TEXT.zonedefinition.title}</h4>
                <div className='sub'>{TEXT.zonedefinition.sub}</div>
                <div className='imageContainer'>
                  {TEXT.zonedefinition.image}
                </div>
                {TEXT.zonedefinition.points.map((p, i) => {
                  return (
                    <div className='point' key={i}>
                      <h5>{p[0]} </h5>

                      {p[1]}
                    </div>
                  )
                })}
              </div>
              <div className='contentafterwrapper'>{TEXT.contentafter}</div>
              <Hint big>{TEXT.reply}</Hint>
            </div>
          </div>
        </div>
        <Footer
          mailTo={language === 'fr' ? 'Duc-Quang.Nguyen@tamedia.ch' : undefined}
          hidePortfolioTeaser={language === 'fr' ? true : false}
          questions={LOCALES.questions}
          articleId={ARTICLE_ID}
          credits={[
            [
              LOCALES.other.credits[0],
              'Dominik Balmer, Sylvain Besson, Dominique Botti, Oliver Zihlmann'
            ],
            [LOCALES.other.credits[1], 'Patrick Meier, Duc-Quang Nguyen'],
            [LOCALES.other.credits[2], 'Sebastian Broschinski']
          ]}
          locale={language}
        >
          {ARTICLETEASER.length > 0 && (
            <div className='ui container' style={{ marginBottom: '2em' }}>
              <h4>{LOCALES.other.teaserTitle}</h4>
              <ArticleTeasers
                numberOfCards='three'
                articleIds={ARTICLETEASER}
              />
            </div>
          )}
          <div className='ui vertical grey segment methodik'>
            <div className='ui text container'>
              <div className='title'>{TEXT.technical_head}</div>
              <div className='methodikText'>{TEXT.technical}</div>
              <br />
              <SourceTable sources={SOURCEDATA} locales={LOCALES} />
            </div>
          </div>
        </Footer>
      </>
      {/* )} */}
    </div>
  )
}
